import { NgClass } from '@angular/common';
import { Component, input } from '@angular/core';
import { PercentageCircleColour } from '@shared/types';
import { HlmCardModule } from '@spartan-ng/card';
import { HlmIconComponent } from '@spartan-ng/icon';
import { PercentageCircleComponent } from './percentage-circle.component';

/**
 * @component StatCardComponent
 * @description Displays a statistical card with dynamic content including title, count, icon, and an optional percentage circle.
 *
 * @input title - The title of the stat card, required.
 * @input count - The numerical count to display, required.
 * @input iconName - The name of the icon to display, required.
 * @input percentage - The percentage value for the optional percentage circle.
 * @input percentageLabel - The label for the percentage circle color scheme.
 * @input loading - Flag to indicate if the percentage circle is in a loading state.
 * @input colorScheme - Object containing color scheme information for the card, required.
 */
@Component({
  selector: 'dxc-stat-card',
  imports: [
    NgClass,
    HlmIconComponent,
    HlmCardModule,
    PercentageCircleComponent
  ],
  template: `
    <section
      hlmCard
      class="border-border bg-background relative overflow-hidden rounded-xl border shadow-xs before:absolute before:end-0 before:top-0 before:h-full before:w-full before:bg-gradient-to-br before:via-transparent before:blur-xl dark:before:via-transparent"
      [ngClass]="getSectionClasses()"
    >
      <div class="relative z-10 flex justify-between">
        <div class="flex flex-col gap-y-1">
          <div
            hlmCardHeader
            class="gap-4 p-0"
            [direction]="size() === 'xs' ? 'row' : 'column'"
          >
            <span
              class="bg-background text-text inline-flex items-center justify-center rounded-lg shadow"
              [ngClass]="{
                'h-4 w-4 md:h-6 md:w-6': size() === 'xs',
                'h-6 w-6 md:h-8 md:w-8': size() === 'small',
                'h-8 w-8 md:h-10 md:w-10': size() === 'default'
              }"
            >
              <!-- Dynamic icon component with conditional color class -->
              <hlm-icon
                class="flex-shrink-0"
                [name]="iconName()"
                [ngClass]="colorScheme().iconColor"
                [size]="getIconSize()"
              />
            </span>
            <h3
              hlmCardTitle
              class="font-normal"
              [ngClass]="{ 'text-sm': size() === 'small' || size() === 'xs' }"
            >
              {{ title() }}
            </h3>
          </div>
          <p
            hlmCardContent
            class="p-0 font-semibold"
            [ngClass]="{
              'text-lg': size() === 'xs',
              'text-xl': size() === 'small',
              'text-2xl': size() === 'default'
            }"
          >
            {{ count() }}
          </p>
        </div>

        <!-- Conditional rendering of Percentage Circle based on input -->
        @if (showPercentageCircle()) {
          <dxc-percentage-circle
            [label]="percentageLabel()!"
            [loading]="loading()"
            [percentage]="percentage()!"
            [size]="percentageCircleSize()"
          />
        }
      </div>
    </section>
  `
})
export class StatCardComponent {
  public size = input<'default' | 'small' | 'xs'>('default');
  public title = input.required<string>();
  public count = input.required<number>();
  public iconName = input.required<string>();
  public percentage = input<number>();
  public percentageLabel = input<keyof typeof PercentageCircleColour>();
  public percentageCircleSize = input<string>('110');
  public loading = input<boolean>();
  public colorScheme = input.required<{
    gradientFrom: string;
    darkGradientFrom: string;
    iconColor: string;
  }>();

  /**
   * @method getSectionClasses
   * @description Constructs a string of class names based on the component's colorScheme input.
   * This method is used for dynamic styling of the card's section element.
   * @returns {string} A space-separated string of class names.
   */
  protected getSectionClasses() {
    const padding = this.size() === 'small' ? 'p-3 sm:p-4' : 'p-4 sm:p-5';
    return `${this.colorScheme().gradientFrom} ${this.colorScheme().darkGradientFrom} ${padding}`;
  }

  /**
   * @method showPercentageCircle
   * @description Determines whether the percentage circle should be displayed.
   * The circle is shown only if the percentage input is neither null nor undefined.
   * @returns {boolean} True if the percentage circle should be shown, false otherwise.
   */
  protected showPercentageCircle() {
    return this.percentage() !== null && this.percentage() !== undefined;
  }

  protected getIconSize() {
    if (this.size() === 'xs') {
      return 'xs';
    } else if (this.size() === 'small') {
      return 'sm';
    } else {
      return 'base';
    }
  }
}
